import { cx } from "@emotion/css";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Card,
  Footer,
  Header,
  Main,
  PlainSkeleton,
} from "@/components";
import { Label } from "@/components/form";
import { useFormErrorsToast, useVisibilityToggle } from "@/hooks";
import { useUpdatePatmoPosition } from "@/queries";
import { errorToast, SAVE_SPOT_NAME_FAILED_HINT } from "@/shared";
import { setStorage } from "@/shared/store";

import { EntrancePhotoGuide, ImageControl, SpotInput } from "../components";
import { useDeviceSpotValue } from "../hooks";

interface SpotSettingForm {
  location: string;
  customLocation: string;
  uri: string;
}

export function PATMOSpot() {
  const navigate = useNavigate();
  const {
    watch,
    register,
    setValue,
    getValues,
    setFocus,
    handleSubmit,
    reset,
    control,
  } = useForm<SpotSettingForm>({
    defaultValues: {
      location: "",
      customLocation: "",
      uri: "",
    },
  });
  const { toastFormValueError } = useFormErrorsToast();
  const { isLoading: isGETDeviceLoading } = useDeviceSpotValue(reset);
  const [isAllFieldFilled] = useVisibilityToggle(
    watch("location"),
    // Custom option requires `customLocation`; otherwise use an always-truthy sentinel.
    watch("location") === "カスタム" ? watch("customLocation") : "permanent"
  );

  const { mutateAsync: updatePatmoPositionAsync, isLoading } =
    useUpdatePatmoPosition();

  const handleConfirmClick = async (value: SpotSettingForm) => {
    if (value.location === "カスタム") {
      if (!value.customLocation.trim())
        return errorToast("設置場所(識別名)は必須です。");
      else {
        setStorage("spotName", value.customLocation.trim());
      }
    } else {
      setStorage("spotName", value.location);
    }
    setStorage("uri", value.uri);

    updatePatmoPositionAsync()
      .then(() => {
        navigate(-1);
      })
      .catch((error) => {
        if (error?.status === 400) {
          errorToast(SAVE_SPOT_NAME_FAILED_HINT);
        } else {
          errorToast(error?.message ?? error?.toString());
        }
      });
  };

  useEffect(() => {
    if (watch("location") === "カスタム") {
      setFocus("customLocation");
    }
  }, [watch("location")]);

  if (isGETDeviceLoading) {
    return (
      <>
        <Header>設置場所（識別名）設定</Header>
        <Main>
          <Card className="!p-[16px]">
            <PlainSkeleton />
          </Card>
        </Main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header>設置場所（識別名）設定</Header>
      <Main>
        <Card className="!p-[16px]">
          <EntrancePhotoGuide />
          <div>
            <Label htmlFor="" required>
              設置場所（識別名）
            </Label>
            <SpotInput
              watch={watch}
              register={register}
              setValue={setValue}
              getValues={getValues}
            />
            <Label htmlFor="" className="mt-5 [&_p]:font-semibold">
              写真
            </Label>
            <p
              className={cx(
                "mb-3 text-xs font-normal leading-[18px] text-neutral-400"
              )}
            >
              画像は最大16MBです。横向きの写真を撮ってください。
              <br />
              下部に写真の注意事項があります。
            </p>
            <ImageControl name="uri" control={control} />
            <div className="mt-6">
              <Button
                className="w-full"
                onClick={handleSubmit(handleConfirmClick, toastFormValueError)}
                disabled={!isAllFieldFilled}
                loading={isLoading}
              >
                確認して提出
              </Button>
            </div>
          </div>
        </Card>
      </Main>
      <Footer />
    </>
  );
}
